import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Music, Calendar, GraduationCap, FileText, LogOut } from "lucide-react";
import { Badge } from "@/components/ui/badge";



interface Profile {
  name: string | null;
  email: string;
}

interface Submission {
  id: string;
  event_date: string;
  venue_address: string;
  created_at: string;
  event_dates_json?: any;
}


const Dashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      navigate("/auth");
      return;
    }

    // Load profile
    const { data: profileData } = await supabase
      .from("profiles")
      .select("name, email")
      .eq("id", session.user.id)
      .single();

    if (profileData) {
      setProfile(profileData);
    }

    // Load submissions
    const { data: submissionsData } = await supabase
      .from("contact_submissions")
      .select("id, event_date, venue_address, created_at, event_dates_json")
      .eq("user_id", session.user.id)
      .order("created_at", { ascending: false });

    if (submissionsData) {
      setSubmissions(submissionsData);
    }

    setIsLoading(false);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    toast({
      title: "Signed out",
      description: "You've been successfully signed out.",
    });
    navigate("/");
  };

  const handleNewInquiry = async () => {
    // Check if user has a complete profile
    const { data: { session } } = await supabase.auth.getSession();
    
    if (session?.user?.id) {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .single();

      // If profile exists with required info, skip to event details
      if (profileData && profileData.name && profileData.email && profileData.phone) {
        // Clear previous inquiry's songs from database
        const sessionId = sessionStorage.getItem('session_id') || crypto.randomUUID();
        await supabase
          .from('favorite_songs')
          .delete()
          .eq('session_id', sessionId);
        
        // Generate new session ID for fresh start
        const newSessionId = crypto.randomUUID();
        sessionStorage.setItem('session_id', newSessionId);
        
        navigate("/event-info");
        return;
      }
    }
    
    // Clear and create new session for non-authenticated users too
    const newSessionId = crypto.randomUUID();
    sessionStorage.setItem('session_id', newSessionId);
    
    // Otherwise start from contact form
    navigate("/");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={() => navigate("/")}>
            <Music className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-bold">The Lost Strings</h1>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              {profile?.name || profile?.email}
            </span>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">Welcome back!</h2>
          <p className="text-muted-foreground">
            Explore exclusive content, book events, and enjoy member benefits.
          </p>
        </div>

        <Tabs defaultValue="inquiries" className="space-y-6">
          <TabsList>
            <TabsTrigger value="inquiries">
              <FileText className="h-4 w-4 mr-2" />
              My Inquiries
            </TabsTrigger>
            <TabsTrigger value="concerts">
              <Calendar className="h-4 w-4 mr-2" />
              Concerts & Events
            </TabsTrigger>
            <TabsTrigger value="lessons">
              <GraduationCap className="h-4 w-4 mr-2" />
              Music Lessons
            </TabsTrigger>
            <TabsTrigger value="music">
              <Music className="h-4 w-4 mr-2" />
              Stream Music
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inquiries">
            <Card>
              <CardHeader>
                <CardTitle>Your Event Inquiries</CardTitle>
                <CardDescription>
                  View and manage your booking requests
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={handleNewInquiry} className="w-full sm:w-auto">
                  Submit Another Inquiry
                </Button>
                
                {submissions.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No inquiries yet. Submit your first event inquiry!
                  </p>
                ) : (
                  <div className="space-y-3">
                    {submissions.map((submission) => (
                      <Card key={submission.id}>
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              {submission.event_dates_json && submission.event_dates_json.length > 0 ? (
                                <>
                                  <p className="font-semibold mb-2">
                                    Event Dates ({submission.event_dates_json.length}):
                                  </p>
                                  <div className="space-y-2">
                                    {submission.event_dates_json.map((evt, idx) => (
                                      <div key={idx} className="text-sm">
                                        <p className="font-medium">
                                          {new Date(evt.date).toLocaleDateString('en-US', { 
                                            weekday: 'short',
                                            month: 'short', 
                                            day: 'numeric',
                                            year: 'numeric'
                                          })}
                                        </p>
                                        <p className="text-muted-foreground text-xs">
                                          {evt.loadInTime && `Load-in: ${evt.loadInTime} • `}
                                          Event: {evt.startTime} - {evt.endTime}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                </>
                              ) : (
                                <p className="font-semibold">
                                  Event Date: {new Date(submission.event_date).toLocaleDateString()}
                                </p>
                              )}
                              <p className="text-sm text-muted-foreground mt-2">
                                {submission.venue_address}
                              </p>
                              <p className="text-xs text-muted-foreground mt-2">
                                Submitted: {new Date(submission.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge variant="secondary">Pending</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="concerts">
            <Card>
              <CardHeader>
                <CardTitle>Exclusive Concerts & Events</CardTitle>
                <CardDescription>
                  Discover upcoming performances and exclusive member events
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-center py-8">
                  Concert listings coming soon! Check back for exclusive member events.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="lessons">
            <Card>
              <CardHeader>
                <CardTitle>Music Lessons</CardTitle>
                <CardDescription>
                  Learn from our talented roster of musicians
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-center py-8">
                  Music lesson catalog coming soon! We'll offer lessons in various instruments.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="music">
            <Card>
              <CardHeader>
                <CardTitle>Stream Music</CardTitle>
                <CardDescription>
                  Listen to exclusive tracks from our roster musicians
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-center py-8">
                  Music streaming coming soon! Enjoy exclusive content from The Lost Strings.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>
      </main>
    </div>
  );
};

export default Dashboard;
